
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { KanbanCard } from "@/types/database";
import { Loader2 } from "lucide-react";

interface TrelloCardDialogProps {
  mode: "create" | "edit";
  boardId: string;
  columnId: string;
  card?: KanbanCard;
  onCardSaved: () => void;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function TrelloCardDialog({
  mode,
  boardId,
  columnId,
  card,
  onCardSaved,
  trigger,
  open,
  onOpenChange
}: TrelloCardDialogProps) {
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    if (mode === "edit" && card) {
      setTitle(card.title);
    } else {
      setTitle("");
    }
  }, [mode, card]);
  
  const handleSaveCard = async () => {
    if (!title.trim()) {
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (mode === "create") {
        // Get the last position in the column
        const { data: columnCards } = await supabase
          .from('kanban_cards')
          .select('id')
          .eq('column_id', columnId)
          .eq('is_archived', false);
        
        const position = columnCards ? columnCards.length : 0;
        
        // Create new card with just the title
        const { error } = await supabase
          .from('kanban_cards')
          .insert({
            column_id: columnId,
            title,
            position: position
          });
          
        if (error) throw error;
        
        // Success notification removed
      } else if (mode === "edit" && card) {
        // Update existing card title
        const { error } = await supabase
          .from('kanban_cards')
          .update({
            title
          })
          .eq('id', card.id);
          
        if (error) throw error;
        
        // Success notification removed
      }
      
      onCardSaved();
      if (onOpenChange) onOpenChange(false);
    } catch (error) {
      console.error('Error saving card:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={onOpenChange}
    >
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Adicionar Cartão" : "Editar Cartão"}</DialogTitle>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid items-center gap-4">
            <Input 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título do cartão"
              className="w-full" 
              autoFocus
            />
          </div>
        </div>
        
        <div className="flex justify-end">
          <Button type="submit" onClick={handleSaveCard} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              mode === "create" ? "Adicionar" : "Salvar"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
